import { useQuery } from "@tanstack/react-query";
import QRCode from "qrcode";
import React, { useEffect, useRef, useState } from "react";
import ReactGA from "react-ga4";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";

import { ReviewRequestPrintPreview } from "./ReviewRequestPrintPreview";
import { useImpersonate } from "../../../hooks/impersonate";
import { useMe } from "../../../hooks/queries";
import { LocationData } from "../../../models/LocationData";
import { LocationReviewRequestPrintsData } from "../../../models/LocationReviewRequestPrints";
import DataSvc from "../../../services/dataSvc";

import "./styles.scss";

interface ReviewRequestPrintProps {
  locationData: LocationData;
}

export const ReviewRequestPrint: React.FC<ReviewRequestPrintProps> = ({
  locationData,
}) => {
  const { t: _t } = useTranslation();
  const t = (key: string) =>
    _t(`facilityDetailsPage.reviewRequestPrint.${key}`);

  const { impersonateStatus } = useImpersonate();
  const { data: me } = useMe();
  const organizationId =
    me?.type === "org_admin" ? me.organization_id : undefined;

  const qrCodeCanvasRef = useRef<HTMLCanvasElement>(null);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string | null>(null);
  const qrCodeUrl = `https://search.google.com/local/writereview?placeid=${locationData.gbp_place_id}`;

  const { data: printsData } = useQuery<LocationReviewRequestPrintsData>(
    ["location-review-request-prints", locationData.id, impersonateStatus],
    () => DataSvc.getLocationReviewRequestPrints(Number(locationData.id))
  );

  // Generate QR code for direct download
  useEffect(() => {
    if (qrCodeCanvasRef.current && qrCodeUrl) {
      QRCode.toCanvas(
        qrCodeCanvasRef.current,
        qrCodeUrl,
        { scale: 16 },
        (error) => {
          if (!error && qrCodeCanvasRef.current) {
            const dataUrl = qrCodeCanvasRef.current.toDataURL("image/png");
            setQrCodeDataUrl(dataUrl);
          }
        }
      );
    }
  }, [qrCodeUrl]);

  const handleDownloadQRCode = () => {
    ReactGA.event({
      category: "facility-details",
      action: "Download QR code from Review Request Print",
    });
  };

  return (
    <div className="white-border-panel review-request-print-section padding32">
      <div className="section-header">
        <h3 className="section-title">{t("section_title")}</h3>
        <div className="section-description">{t("section_description")}</div>
      </div>

      <div className="content">
        <div className="preview-container">
          {printsData && (
            <ReviewRequestPrintPreview
              size="card"
              qrCodeUrl={qrCodeUrl}
              options={printsData.card_size_options}
            />
          )}
        </div>

        <div className="actions">
          <NavLink
            to={`/location/${locationData.id}${
              organizationId ? `/organization/${organizationId}` : ""
            }/review-request-prints`}
            className="btn btn-blue"
          >
            {t("customize_and_download")}
          </NavLink>

          <div className="qr-code-download">
            <canvas ref={qrCodeCanvasRef} style={{ display: "none" }}></canvas>
            <a
              className="btn btn-border"
              download={`${locationData.name}__Review_Request_QR_CODE.png`}
              href={qrCodeDataUrl || undefined}
              target="_blank"
              rel="noreferrer"
              onClick={handleDownloadQRCode}
            >
              <i className="icon icon-saveload icon-saveload-blue"></i>
              {t("download_qr_code")}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};
